import { FormGroup } from '@angular/forms';
import Swal from 'sweetalert2';
import { AutorizacionesService } from '../../core/services/autorizaciones.service';

/**
 * Clase utilitaria para manejar la lógica del formulario y mapeo de datos
 */
export class FormularioUtils {

  /**
   * Función para mapear los datos del formulario al modelo requerido por el backend
   */
  static mapFormToBackendModel(formData: any, farmacias: any[], instituciones: any[]): any {
    const tipoAutorizacion = Number(formData.tipoAutorizacion);

    const farmaciaSeleccionada = farmacias.find(f => f.id === Number(formData.farmacia));

    const institucionSeleccionada = instituciones.find(i => {
      const idBuscado = Number(tipoAutorizacion === 2 ? formData.institucion : formData.institucionCir);
      return i.id === idBuscado;
    });

    const backendModel = {
      tipoDoc: formData.tipoDocumento,
      nroDoc: formData.dni,
      apellido: formData.apellido,
      nombre: formData.nombre,
      email: formData.email,
      telefono: formData.codigoArea + formData.numeroTelefono,
      idAutorizar: tipoAutorizacion,
      diagnostico: FormularioUtils.getDiagnostico(formData),
      prescripMedicPrim: formData.prescripMedicPrim || null,
      nroCuitFarmacia: farmaciaSeleccionada ? farmaciaSeleccionada.cuit : null,
      sucursalFarmacia: farmaciaSeleccionada ? farmaciaSeleccionada.sucursal : null,
      turnoAsignado: FormularioUtils.getTurnoAsignado(formData),
      fechaTurnoAsignado: FormularioUtils.getFechaTurno(formData),
      idPrestador: institucionSeleccionada ? institucionSeleccionada.id : null,
      prestador: institucionSeleccionada ? institucionSeleccionada.apellidoRazonSocial : null,
      matProtCirug: formData.necesitaMateriales || null,
      observaciones: FormularioUtils.getObservaciones(formData)
    };

    return backendModel;
  }

  /**
   * Procesa el envío completo del formulario (datos + archivos en una sola llamada)
   */
  static procesarEnvioCompleto(
    authForm: FormGroup,
    farmacias: any[],
    instituciones: any[],
    selectedFiles: any,
    autorizacionesService: AutorizacionesService,
    callbackExito: () => void,
    callbackReset: () => void
  ): void {
    const backendData = FormularioUtils.mapFormToBackendModel(authForm.value, farmacias, instituciones);
    const todosLosArchivos = FormularioUtils.recolectarArchivos(selectedFiles);
    const documento = authForm.value.dni;

    /** Validación final antes del POST: verificar que haya archivos */
    if (!todosLosArchivos || todosLosArchivos.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Debe adjuntar al menos un archivo para continuar.',
        confirmButtonColor: '#d33'
      });
      return;
    }

    Swal.fire({
      title: 'Enviando solicitud',
      html: 'Por favor espera mientras se procesa tu información...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    autorizacionesService.enviarSolicitudCompleta(backendData, todosLosArchivos, documento)
      .subscribe({
        next: () => {
          callbackExito();
          callbackReset();
        },
        error: () => {
          // El error ya se maneja en el servicio
        }
      });
  }

  /**
   * Verifica si faltan archivos obligatorios según el tipo de autorización
   */
  static hayArchivosObligatoriosFaltantes(authForm: FormGroup, selectedFiles: any): boolean {
    const tipo = Number(authForm.get('tipoAutorizacion')?.value);

    switch (tipo) {
      case 1:
        return selectedFiles.recetasFiles.length === 0;
      case 2:
        return selectedFiles.pedidosEstFiles.length === 0;
      case 3:
        if (authForm.get('necesitaMateriales')?.value === 'S') {
          return selectedFiles.pedidosCirFiles.length === 0 ||
            selectedFiles.pedidosMatFiles.length === 0 ||
            selectedFiles.estudiosCircFiles.length === 0;
        } else {
          return selectedFiles.pedidosCirFiles.length === 0 ||
            selectedFiles.estudiosCircFiles.length === 0;
        }
      case 4:
        return selectedFiles.pedidosOxiFiles.length === 0;
      default:
        return false;
    }
  }

  /**
   * Muestra el mensaje de éxito después de enviar la solicitud
   */
  static mostrarExito(): void {
    Swal.fire({
      title: '¡Solicitud enviada!',
      text: 'Recibirás una respuesta por correo electrónico en un plazo de hasta 72 horas hábiles.',
      icon: 'success',
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#3085d6'
    });
  }

  /**
   * Maneja el evento keydown para que Enter funcione como Tab
   */
  static manejarEnterComoTab(event: KeyboardEvent): void {
    const elemento = event.target as HTMLElement;
    const tagName = elemento.tagName.toLowerCase();

    if (!['input', 'select'].includes(tagName) ||
      (tagName === 'input' && (elemento as HTMLInputElement).type === 'submit')) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      const formulario = elemento.closest('form');
      if (!formulario) return;

      const elementosFocusables = formulario.querySelectorAll(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), button:not([disabled]):not([type="submit"])'
      );

      const elementos = Array.from(elementosFocusables).filter(
        (el) => (el as HTMLElement).offsetParent !== null
      ) as HTMLElement[];

      const indiceActual = elementos.indexOf(elemento);

      if (indiceActual !== -1 && indiceActual < elementos.length - 1) {
        elementos[indiceActual + 1].focus();
      }
    }
  }

  // ==========================================
  // MÉTODOS PRIVADOS AUXILIARES
  // ==========================================

  private static getDiagnostico(formData: any): string {
    const tipoAutorizacion = Number(formData.tipoAutorizacion);

    switch (tipoAutorizacion) {
      case 1: return formData.diagnosticoMed || '';
      case 2: return formData.diagnosticoEst || '';
      case 3: return formData.diagnosticoCir || '';
      case 4: return formData.diagnosticoOxi || '';
      default: return '';
    }
  }

  private static getTurnoAsignado(formData: any): string {
    const tipoAutorizacion = Number(formData.tipoAutorizacion);

    if (tipoAutorizacion === 2) {
      return formData.tieneTurno || 'N';
    } else if (tipoAutorizacion === 3) {
      return formData.tieneTurnoCir || 'N';
    }
    return 'N';
  }

  private static getFechaTurno(formData: any): string | null {
    const tipoAutorizacion = Number(formData.tipoAutorizacion);

    if (tipoAutorizacion === 2 && formData.tieneTurno === 'S') {
      return formData.fechaTurno;
    } else if (tipoAutorizacion === 3 && formData.tieneTurnoCir === 'S') {
      return formData.fechaTurnoCir;
    }
    return null;
  }

  private static getObservaciones(formData: any): string {
    const tipoAutorizacion = Number(formData.tipoAutorizacion);

    switch (tipoAutorizacion) {
      case 1: return formData.observacionesMed || '';
      case 2: return formData.observacionesEst || '';
      case 3: return formData.observacionesCir || '';
      case 4: return formData.observacionesOxi || '';
      default: return '';
    }
  }

  private static recolectarArchivos(selectedFiles: any): File[] {
    let todosLosArchivos: File[] = [];

    Object.keys(selectedFiles).forEach(key => {
      if (Array.isArray(selectedFiles[key])) {
        todosLosArchivos = todosLosArchivos.concat(selectedFiles[key]);
      }
    });

    return todosLosArchivos;
  }
}